import { useEffect, useRef } from 'react';
import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

type Guard = {
  isDirty: () => boolean;
  message: MessageDescriptor;
};

const guards = new Set<Guard>();
const DEFAULT_MESSAGE = msg`You have unsaved changes. Leave this page?`;

export const registerUnsavedGuard = (
  isDirty: () => boolean,
  message: MessageDescriptor = DEFAULT_MESSAGE,
): (() => void) => {
  const guard: Guard = { isDirty, message };
  guards.add(guard);
  return () => {
    guards.delete(guard);
  };
};

export const runUnsavedGuards = (
  confirmFn: (message: string) => boolean = (message) => window.confirm(message),
  i18n: I18n,
): boolean => {
  const dirtyMessages = [...guards]
    .filter((guard) => guard.isDirty())
    .map((guard) => i18n._(guard.message));
  if (dirtyMessages.length === 0) return true;
  if (dirtyMessages.length === 1) return confirmFn(dirtyMessages[0]);
  const changes = dirtyMessages.map((message, index) => `${index + 1}. ${message}`).join('\n');
  return confirmFn(i18n._(msg`You have unsaved changes:\n${changes}\n\nLeave this page?`));
};

export const _clearUnsavedGuardsForTest = (): void => {
  guards.clear();
};

export const useUnsavedChangesGuard = (
  isDirty: () => boolean,
  message?: MessageDescriptor,
): void => {
  // Registered guards must read the latest render without changing their order.
  const isDirtyRef = useRef(isDirty);
  const messageRef = useRef(message);
  isDirtyRef.current = isDirty;
  messageRef.current = message;
  useEffect(() => {
    const guard: Guard = {
      isDirty: () => isDirtyRef.current(),
      get message() {
        return messageRef.current ?? DEFAULT_MESSAGE;
      },
    };
    guards.add(guard);
    return () => {
      guards.delete(guard);
    };
  }, []);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
};
